import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { NewsStoreContext } from "../../Store/NewsStore";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Typography, Button, Chip, Avatar } from "@mui/material";
import { getNews } from "../../Requests/NewsService";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import AddModalView from "../AddModalView/AddModalView";
import AddNews from "./AddNews";
import { VISIBLE_STATE_CASES } from "../../Models/StateConstants.ts";
import { useRestoreScrollPosition } from "../../Hooks/useScrollRestoration";
import { useTableState } from "../../Hooks/useTableState";
import { useFilteredBySearch } from "../../Hooks/useFilteredBySearch";
import TableToolbarWithSearch from "../TableToolbar/TableToolbarWithSearch.jsx";
import ColumnsIcon from "../TableToolbar/ColumnsIcon.jsx";


const COLUMNS = [
  {
    accessorKey: 'title',
    header: 'Title'
  },
  {
    accessorKey: 'source',
    header: 'Source',
    enableGlobalFilter: false,
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.newsDate),
    accessorKey: 'newsDate',
    header: 'Date',
    filterVariant: 'date-range',
    enableGlobalFilter: false,
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
  {
    accessorFn: (originalRow) => originalRow.mentionedMembers,
    header: 'Member Mention',
    enableGlobalFilter: false,
    enableColumnFilter: false,
    Cell: ({ cell }) => (
      <Box>
        {
          cell.getValue().map((member) => (
            <Chip
              key={member.obgectId}
              avatar={
                <Avatar src={member.avatar} />
              }
              label={member.name + " " + member.surname}
            />
          ))
        }
      </Box>
    )
  },
  {
    accessorKey: 'hiddenState',
    header: 'Visibility',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
    Cell: ({ cell }) => (
      <Chip label={cell.getValue()} />
    )
  },
]

const SEARCH_FIELD_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'source', label: 'Source' },
  { value: 'mentionedMembers', label: 'Member mention' }
]

export default function NewsPage() {

  const store = useContext(NewsStoreContext)

  const [columnVisibility, setColumnVisibility] = useState(store.columnVisibility)
  useEffect(() => {
    store.columnVisibility = columnVisibility
  }, [columnVisibility, store])

  const [columnFilters, setColumnFilters] = useState(store.columnFilters)
  useEffect(() => {
    store.columnFilters = columnFilters
  }, [columnFilters, store])
  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  const [pagination, setPagination] = useState(store.pagination)
  useEffect(() => {
    store.pagination = pagination
  }, [pagination, store])

  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  // Use store for state preservation
  const [open, setOpen] = useState(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [tableData, setTableData] = useState(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const fetchData = useCallback(async () => {
    const result = await getNews()
    setIsloading(false)
    setIsRefetching(false)
    setTableData(result)
  }, [])

  useEffect(() => {
    // Restore state from store if data exists
    if (store.tableData.length > 0 && !store.needsRefresh) {
      setTableData(store.tableData)
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchData()
    }
  }, [store, fetchData])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const columns = useMemo(() => COLUMNS, [])
  const getRowSearchValue = useMemo(
    () => (row, field) => {
      if (field === 'mentionedMembers') {
        const members = Array.isArray(row.mentionedMembers) ? row.mentionedMembers : []
        return members.length > 0
          ? members
            .map((m) => `${m.name || ''} ${m.surname || ''}`.trim())
            .filter(Boolean)
            .join(' ')
          : ''
      }
      return row[field]
    },
    []
  )
  const data = useFilteredBySearch(tableData, globalFilter, searchField, getRowSearchValue)

  const table = useMaterialReactTable({
    columns,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    muiFilterDatePickerProps: {
      format: 'DD/MM/YYYY',
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/news/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        exportFileName="news"
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={140}
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add News
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingTop={2}
        paddingBottom={1}
      >
        <Typography
          variant="h4"
        >
          News
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add News"}
        view={
          <AddNews publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
